import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import {
  Briefcase, Calendar, FileText, Mail, MoreHorizontal, Phone, User, CheckSquare,
} from "lucide-react"
import { parseServerDate } from "@/lib/utils"

/**
 * Beziehungs-Graph: Name eingeben, Zusammenhaenge sehen.
 *
 * Layout ist ein radialer Baum, kein Kraeftesimulation-Wackelbild: Person in
 * der Mitte, ihre Vorgaenge im ersten Ring, alles was daran haengt im zweiten.
 * Jeder Vorgang bekommt einen Winkelsektor, dessen Groesse sich nach der Zahl
 * seiner Kinder richtet — ein Vorgang mit zwoelf Belegen wird also breiter
 * gezeichnet als einer mit einem.
 *
 * WARUM DETERMINISTISCH: Ein Kraefte-Layout ordnet die Knoten bei jedem Laden
 * anders an. Wer denselben Kunden zweimal aufruft, soll dasselbe Bild sehen —
 * sonst muss er es jedes Mal neu lesen.
 *
 * Bewusst ohne Graph-Bibliothek: das ist reines SVG, es gibt nichts, was eine
 * Abhaengigkeit hier besser koennte.
 */

export type GraphNode = {
  id: string
  kind: "contact" | "project" | "email" | "call" | "document" | "task" | "appointment" | "more"
  label: string
  sublabel?: string
  ref_id?: number | null
  date?: string
  /** Vorgang abgeschlossen (Pipeline-Code ≥ 2000). */
  closed?: boolean
}
export type GraphEdge = { from: string; to: string; label?: string }
export type Graph = { nodes: GraphNode[]; edges: GraphEdge[] }

const FARBE: Record<GraphNode["kind"], string> = {
  contact: "#1e293b",
  project: "#0284c7",
  email: "#4f46e5",
  call: "#16a34a",
  document: "#d97706",
  task: "#db2777",
  appointment: "#0891b2",
  more: "#94a3b8",
}

const ART_NAME: Record<GraphNode["kind"], string> = {
  contact: "Person",
  project: "Vorgang",
  email: "E-Mail",
  call: "Anruf",
  document: "Beleg",
  task: "Aufgabe",
  appointment: "Termin",
  more: "weitere",
}

export function KindIcon({ kind, size = 13, color }: { kind: GraphNode["kind"]; size?: number; color?: string }) {
  const style = { width: size, height: size, color: color ?? FARBE[kind], flexShrink: 0 }
  switch (kind) {
    case "contact": return <User style={style} />
    case "project": return <Briefcase style={style} />
    case "email": return <Mail style={style} />
    case "call": return <Phone style={style} />
    case "document": return <FileText style={style} />
    case "task": return <CheckSquare style={style} />
    case "appointment": return <Calendar style={style} />
    default: return <MoreHorizontal style={style} />
  }
}

export const GRAPH_FARBEN = FARBE
export const GRAPH_ART_NAMEN = ART_NAME

type Platziert = GraphNode & {
  x: number; y: number; r: number; ring: number
  /** Richtung vom Mittelpunkt aus — Blaetter beschriften radial nach aussen. */
  winkel: number
}

const KNOTEN_R = [26, 15, 7]

/** Eine einzige leere Liste — eine frische `[]` je Render wuerde die
 *  useMemo-Abhaengigkeiten bei jedem Durchlauf brechen. */
const LEER: Platziert[] = []

// Platz, den die Blatt-Beschriftungen links und rechts brauchen. Der Graph
// rechnet seine Radien so aus, dass die Texte hineinpassen — sonst werden sie
// am Rahmen abgeschnitten.
const TEXT_RAND_X = 130
const TEXT_RAND_Y = 34

// Wie weit ein Zweig seine Blaetter maximal auffaechern darf (75°). Mehr, und
// die aeusseren Blaetter stehen so schraeg zum Zweig, dass die Zugehoerigkeit
// nicht mehr zu sehen ist.
const MAX_FAECHER = (75 * Math.PI) / 180

// Um wie viel jedes zweite Blatt weiter innen sitzt — gegen sich
// ueberlagernde Beschriftungen.
const VERSATZ_ANTEIL = 0.17

// Anteil der Fensterhoehe, den das Netz bekommt.
//
// 🔴 Bewusst ein ANTEIL, nicht „Fensterhoehe minus Position des Feldes".
// Der erste Versuch rechnete mit `getBoundingClientRect().top` — das ist die
// Position im sichtbaren Bereich und haengt damit am Scrollstand. Auf dev
// gemessen: derselbe Graph ergab 593 px weit oben und 519 px weiter unten auf
// der Seite, obwohl sich am Fenster nichts geaendert hatte. Ein Anteil ist
// vorhersehbar und springt nicht beim Scrollen.
const HOEHE_ANTEIL = 0.68

// Ab hier bringt mehr Hoehe nichts mehr: das Netz wuerde nur in die Laenge
// gezogen, ohne dass zusaetzliche Knoten Platz faenden.
const MAX_HOEHE = 820

/** Kurzes Datum fuer die Knoten: „07.08." bzw. „07.08.25" im Vorjahr.
 *
 *  Ohne Datum ist am Bild nicht zu erkennen, ob eine Mail von gestern oder aus
 *  dem Frühjahr stammt — und genau das ist beim Blick auf einen Verlauf die
 *  erste Frage. */
function kurzDatum(iso?: string): string {
  if (!iso) return ""
  const d = new Date(iso.includes("T") && !/[+Z]/.test(iso) ? iso + "Z" : iso)
  if (isNaN(d.getTime())) return ""
  const tag = String(d.getDate()).padStart(2, "0")
  const monat = String(d.getMonth() + 1).padStart(2, "0")
  const jahr = d.getFullYear()
  return jahr === new Date().getFullYear()
    ? `${tag}.${monat}.`
    : `${tag}.${monat}.${String(jahr).slice(2)}`
}

/** Zusaetzlicher Abstand nach aussen, wenn unter der Beschriftung noch eine
 *  Datumszeile folgt.
 *
 *  Noetig nur oben und unten: dort zeigt die radiale Richtung senkrecht, und
 *  die zweite Zeile (immer 10 px tiefer) waere sonst im Kreis gelandet. Links
 *  und rechts stoert sie nicht, darum die Gewichtung mit |sin|. */
function versatzAussen(p: { date?: string; winkel: number }): number {
  return p.date ? 9 * Math.abs(Math.sin(p.winkel)) : 0
}

/** Kuerzt Beschriftungen — ein Label, das ueber den Nachbarknoten laeuft,
 *  macht das Bild schlechter lesbar als gar keins. */
function kurz(s: string, max: number): string {
  const t = (s || "").trim()
  return t.length > max ? t.slice(0, max - 1) + "…" : t
}

/**
 * Ordnet jedem datierten Knoten seine Position in der Zeit zu.
 *
 * Das Netz zeigt, WAS zusammenhaengt — die Punkte stehen nach Zugehoerigkeit,
 * nicht nach Zeit. Ein Zeitstrahl darunter half nicht: die Ereignisse liegen
 * ungleich verteilt, an echten Daten lagen drei Knoten bei 0 / 7 / 100 Prozent
 * einer linearen Achse. Zwei klebten aufeinander, einer am Rand.
 *
 * Deshalb RANG statt Abstand — die Nummer sagt „das dritte von sieben", nicht
 * „14 Tage spaeter". Gegen Deckkraft entschieden, weil sie gleich alte
 * Ereignisse nicht trennen kann: zwei Anrufe am selben Tag sehen gleich aus.
 *
 * 🔑 Nur Ereignisse bekommen eine Nummer. Person, Vorgang und Sammelknoten
 * („Anrufe · 4 ohne Vorgang") tragen kein Datum — sie sind Behaelter, keine
 * Ereignisse, und das ist keine Luecke.
 */
/**
 * Die Nummer am Knoten: das Wievielte war dieses Ereignis.
 *
 * Sitzt auf der INNEREN Seite — die Beschriftung geht bei Blaettern radial nach
 * aussen, dort waere die Marke im Text gelandet. Bei Wurzel und Zweigen steht
 * der Text darunter, also weicht sie nach oben aus.
 *
 * Der letzte Stand ist dunkel gefuellt statt umrandet: „was ist die
 * Reihenfolge" beantworten die Zahlen, „wo stehe ich jetzt" beantwortet der
 * eine dunkle Punkt.
 */
function Zeitmarke({ p, nr, letzter }: { p: Platziert; nr: number; letzter: boolean }) {
  const abstand = p.r + 8
  const x = p.ring >= 2 ? -Math.cos(p.winkel) * abstand : -6
  const y = p.ring >= 2 ? -Math.sin(p.winkel) * abstand : -(p.r + 8)
  return (
    <g transform={`translate(${x},${y})`} style={{ pointerEvents: "none" }}>
      <circle r={7.5} fill={letzter ? "#0f172a" : "#fff"} stroke={letzter ? "#0f172a" : "#cbd5e1"} strokeWidth={1} />
      <text
        y={2.8}
        textAnchor="middle"
        style={{ fontSize: 12, fontWeight: 700, fill: letzter ? "#fff" : "#64748b" }}
      >
        {nr}
      </text>
    </g>
  )
}


/** Wie weit zurueck nummeriert wird. */
export const NUMMERN_FENSTER_MONATE = 12

export function zeitRaenge(
  nodes: GraphNode[],
  jetzt: number = Date.now(),
): { rang: Map<string, number>; letzter: string | null; aeltereOhneNummer: number } {
  const datiert = (nodes || [])
    .filter((n) => n.kind !== "more" && !!n.date)
    .map((n) => ({ id: n.id, t: parseServerDate(n.date)?.getTime() ?? NaN }))
    .filter((n) => !Number.isNaN(n.t))
  // 🔴 Bei gleichem Zeitpunkt entscheidet die id — sonst kaeme dasselbe
  //    Dossier bei jedem Laden in anderer Reihenfolge heraus, und genau das
  //    soll dieses Layout nicht (siehe „WARUM DETERMINISTISCH" oben).
  datiert.sort((a, b) => a.t - b.t || a.id.localeCompare(b.id))

  const letzter = datiert.length ? datiert[datiert.length - 1].id : null

  // 🔑 Nur das letzte Jahr bekommt Nummern.
  //
  // An echten Dossiers gemessen (21.08.2026): Probegend 3 Ereignisse, Gagel 7 —
  // aber Bijedic 26, davon 20 Belege aus 2023 bis 2025. Sechsundzwanzig Zahlen
  // sind kein „auf einen Blick" mehr, und die alten Rechnungen tragen die
  // Nummern, ohne etwas zu erzaehlen. Die Grenze ist inhaltlich, nicht
  // willkuerlich: was aelter als ein Jahr ist, behaelt sein Datum und verliert
  // nur die Nummer.
  const grenze = new Date(jetzt)
  grenze.setMonth(grenze.getMonth() - NUMMERN_FENSTER_MONATE)
  let imFenster = datiert.filter((n) => n.t >= grenze.getTime())
  // Der letzte Stand traegt IMMER eine Nummer — auch bei einem Kontakt, bei
  // dem seit Jahren nichts passiert ist. Sonst zeigt das Netz einen Ring ohne
  // Zahl daneben, und das sieht nach einem Fehler aus.
  if (!imFenster.length && datiert.length) imFenster = [datiert[datiert.length - 1]]

  const rang = new Map<string, number>()
  imFenster.forEach((n, i) => rang.set(n.id, i + 1))
  return { rang, letzter, aeltereOhneNummer: datiert.length - imFenster.length }
}


/** Wie breit ein Text ungefaehr wird. Genau messen ginge nur ueber
 *  `getComputedTextLength()` — das braucht einen gerenderten Knoten und
 *  wuerde je Bild hunderte Layouts erzwingen. Der Schaetzwert reicht: er darf
 *  eher zu gross sein, dann bleibt Luft. */
function textBreite(text: string, fontSize: number): number {
  // 0.55 pro Zeichen: im Browser an 14 echten Labels nachgemessen lag der
  // Faktor zwischen 0.474 und 0.563 (Schnitt 0.516) — leicht darueber zu
  // liegen ist richtig, dann bleibt Luft statt Ueberlappung.
  //
  // 🔴 Plus die weisse Kontur. Jedes Label traegt `strokeWidth: 3.5` als
  // Hintergrund; die belegte Flaeche ist also an JEDER Seite ~3.5 px groesser
  // als die Glyphen. Ohne diesen Zuschlag rechnet die Entzerrung mit zu
  // kleinen Kaesten und laesst Ueberlagerungen stehen, die man deutlich sieht.
  return text.length * fontSize * 0.55 + 2 * KONTUR
}

/** Halbe Breite der weissen Kontur hinter jedem Label. */
const KONTUR = 3.5

/** Die zweite Zeile unter Wurzel und Zweigen — Status und/oder Datum.
 *  Bewusst EINE Quelle fuer Huellenrechnung und Rendering: waeren es zwei
 *  Fassungen, wuerde die Entzerrung wieder gegen einen anderen Text rechnen
 *  als am Ende dasteht. */
/**
 * Wie viele Zeichen ein Zweig-Label tragen darf.
 *
 * Verschieben allein reicht bei einem dicht besetzten Ring nicht: 16 Vorgaenge
 * mit je ~120 px breiten Namen passen nebeneinander schlicht nicht auf den
 * Ring, egal wie man sie senkrecht schiebt. Schmalere Texte kollidieren
 * seltener — und ein gekuerzter Name ist lesbar, zwei uebereinanderliegende
 * sind es nicht.
 *
 * Die Wurzel bleibt unberuehrt: sie steht allein in der Mitte.
 */
function labelMax(p: Platziert, zweige: number): number {
  if (p.ring === 0) return 34
  if (p.ring >= 2) return 24
  if (zweige >= 12) return 16
  if (zweige >= 8) return 21
  return 28
}

function sublabelText(p: Platziert): string {
  if (p.ring >= 2) return ""
  return [kurz(p.sublabel || "", 26), kurzDatum(p.date)].filter(Boolean).join(" · ")
}

/** Grobe Huelle eines Labels in SVG-Koordinaten. */
type LabelHuelle = { id: string; x1: number; x2: number; y1: number; y2: number }

function labelHuelle(p: Platziert, zweige = 0): LabelHuelle | null {
  const text = kurz(p.label || "", labelMax(p, zweige))
  if (!text) return null

  if (p.ring >= 2) {
    const d = p.r + 7 + versatzAussen(p)
    const ax = p.x + Math.cos(p.winkel) * d
    const ay = p.y + Math.sin(p.winkel) * d + (p.date ? -1 : 3.5)
    const b = textBreite(text, 12)
    const nachRechts = Math.cos(p.winkel) >= 0
    return {
      id: p.id,
      x1: nachRechts ? ax : ax - b,
      x2: nachRechts ? ax + b : ax,
      // Datum steht 10 px tiefer und bekommt seine Zeile mitgerechnet.
      y1: ay - 9 - KONTUR,
      y2: ay + (p.date ? 14 : 4) + KONTUR,
    }
  }

  // 🔴 Wurzel und Zweige tragen ZWEI Zeilen: den Namen und darunter
  // Status/Datum. Die zweite Zeile in der Huelle zu vergessen war der Fehler
  // der ersten Fassung — auf dev gemessen blieben 14 Ueberlagerungen uebrig,
  // und es waren fast alle Status-Zeilen („Neu - Erstkontakt" gegen
  // „SUB-3307 — Paul Lux"). Was nicht in der Huelle steht, weicht auch nicht
  // aus.
  const fs = p.ring === 0 ? 15 : 12.5
  const ay = p.y + p.r + (p.ring === 0 ? 20 : 15)
  const haupt = textBreite(text, fs)

  const zweiteZeile = sublabelText(p)
  const unten = zweiteZeile
    ? Math.max(haupt, textBreite(zweiteZeile, p.ring === 0 ? 11.5 : 10.5))
    : haupt
  const b = Math.max(haupt, unten)
  return {
    id: p.id,
    x1: p.x - b / 2,
    x2: p.x + b / 2,
    y1: ay - fs * 0.8 - KONTUR,
    // Die zweite Zeile sitzt bei p.r + 36 bzw. + 29.
    y2: (zweiteZeile ? p.y + p.r + (p.ring === 0 ? 36 : 29) + 4 : ay + 4) + KONTUR,
  }
}

/** Wie weit ein Label hoechstens verschoben wird. Mehr, und es steht weiter
 *  von seinem Knoten weg als vom Nachbarn — dann ist die Zuordnung schlechter
 *  als die Ueberlagerung.
 *
 *  Auf dev nachgemessen: mit 26 blieben bei 16 Vorgaengen an einem Kontakt
 *  11 Ueberlagerungen stehen, weil die Schleife am Deckel abbrach. 44 loest
 *  die dichten Ringe und liegt noch klar unter dem Abstand zum Nachbarknoten. */
const MAX_VERSATZ = 44

/**
 * Rueckt uebereinanderliegende Beschriftungen auseinander (Ticket #20).
 *
 * Die Knoten selbst bleiben, wo sie sind — verschoben wird nur der Text, und
 * nur senkrecht. Waagerecht ginge nicht: die Blaetter beschriften radial nach
 * aussen, ein seitlicher Versatz wuerde den Text vom eigenen Knoten loesen.
 *
 * 🔴 Warum das ueberhaupt noetig ist: `VERSATZ_ANTEIL` zieht jedes zweite
 * Blatt etwas nach innen, das hilft aber nur bei zwei Nachbarn im selben
 * Faecher. Im gemeldeten Fall (Henrik Probefink, 21 Ereignisse) lagen vier Paare
 * uebereinander — Vorgangsnamen unter Belegnamen, quer ueber Zweige hinweg.
 * Deshalb hier gegen ALLE sichtbaren Labels geprueft, nicht nur gegen
 * Geschwister.
 *
 * Verfahren: von oben nach unten, jedes Label gegen die schon gesetzten. Wer
 * kollidiert, rutscht nach unten — gedeckelt, damit nichts davonlaeuft.
 */
function entzerreLabels(platziert: Platziert[], zeigeAlle: boolean): Map<string, number> {
  const versatz = new Map<string, number>()
  const zweige = platziert.filter((p) => p.ring === 1).length
  const huellen = platziert
    .filter((p) => p.ring <= 1 || zeigeAlle)
    .map((p) => labelHuelle(p, zweige))
    .filter(Boolean) as LabelHuelle[]

  // Von oben nach unten. Bei gleicher Hoehe zuerst der weiter links stehende —
  // sonst haengt das Ergebnis an der Reihenfolge im Array und springt.
  huellen.sort((a, b) => (a.y1 - b.y1) || (a.x1 - b.x1))

  const gesetzt: LabelHuelle[] = []
  for (const h of huellen) {
    let dy = 0
    for (let versuch = 0; versuch < 14; versuch++) {
      const stoerer = gesetzt.find(
        (g) => g.x1 < h.x2 && h.x1 < g.x2 && g.y1 < h.y2 + dy && h.y1 + dy < g.y2,
      )
      if (!stoerer) break
      // Genau unter den Stoerer setzen, plus 2 px Luft.
      const noetig = stoerer.y2 + 2 - (h.y1 + dy)
      if (dy + noetig > MAX_VERSATZ) { dy = MAX_VERSATZ; break }
      dy += noetig
    }
    if (dy) versatz.set(h.id, Math.round(dy))
    gesetzt.push({ ...h, y1: h.y1 + dy, y2: h.y2 + dy })
  }
  return versatz
}

export function RelationGraph({
  graph,
  onSelect,
  height = 520,
}: {
  graph: Graph
  onSelect?: (node: GraphNode) => void
  height?: number
}) {
  const [aktiv, setAktiv] = useState<string | null>(null)
  const [alleLabels, setAlleLabels] = useState(false)
  // Zoom + Verschieben. Bei einem Kunden mit drei Vorgaengen und 17 Mails
  // passt nicht mehr alles lesbar auf einen Schirm — man muss hineingehen
  // koennen.
  // 71 % der Sanitec-Vorgaenge sind abgeschlossen — deshalb standardmaessig
  // AUS. Wer sie sehen will, schaltet sie ein.
  //
  // 🔴 AUSSER es sind ALLE: beim Reinigungsmandanten stehen alle 42 Vorgaenge auf
  // „Abgeschlossen" (Prod, 13.08.2026). Der Filter soll Rauschen wegnehmen,
  // nicht das Bild leeren — waere dann nichts mehr uebrig, bleibt alles an.
  const alleAbgeschlossen = useMemo(() => {
    const p = (graph?.nodes || []).filter((n) => n.kind === "project")
    return p.length > 0 && p.every((n) => n.closed)
  }, [graph])
  // Die Reihenfolge haengt an den Rohdaten, nicht am Layout: ein ausgeblendeter
  // Vorgang darf die Nummern der uebrigen Ereignisse nicht verschieben. Sonst
  // heisst derselbe Anruf einmal „3" und nach einem Klick „2".
  const { rang: zeitRang, letzter: letzterKnoten, aeltereOhneNummer } = useMemo(
    () => zeitRaenge(graph?.nodes || []), [graph])
  const [zeigeAbgeschlossene, setZeigeAbgeschlossene] = useState(false)
  useEffect(() => { setZeigeAbgeschlossene(alleAbgeschlossen) }, [alleAbgeschlossen])
  const [versteckteArten, setVersteckteArten] = useState<Set<string>>(new Set())
  const [zoom, setZoom] = useState(1)
  const [versatz, setVersatz] = useState({ x: 0, y: 0 })
  const zieht = useRef<{ x: number; y: number } | null>(null)
  const box = useRef<HTMLDivElement>(null)
  const [breiteVerfuegbar, setBreiteVerfuegbar] = useState(880)
  // Ticket #20: die Hoehe war fest (Prop, 380/520/560), waehrend die Breite
  // gemessen wurde. Auf einem hohen Fenster blieb darunter Platz ungenutzt —
  // genau der „zentrierte" Eindruck aus der Meldung. Der Prop ist jetzt die
  // UNTERGRENZE, gerechnet wird mit dem, was wirklich da ist.
  const [hoeheVerfuegbar, setHoeheVerfuegbar] = useState(height)

  // Der viewBox entspricht exakt der Pixelgroesse des Containers. Damit wird
  // nichts skaliert und die Beschriftungen haben immer ihre echte Groesse.
  //
  // Vorher wuchs der viewBox mit dem Layout, waehrend die Hoehe fest blieb —
  // das SVG rechnete sich dann auf rund 55 % herunter und die Blattnamen waren
  // nicht mehr lesbar. Die Radien richten sich jetzt nach dem Platz, nicht
  // umgekehrt.
  //
  // 🔴 Callback-Ref, NICHT `useEffect(..., [])` auf ein useRef.
  //
  // Vorher hing der Beobachter an `box.current` und wurde einmal beim Mount
  // gesetzt. Beim ersten Render ist `layout` aber noch leer, und dann rendert
  // die Komponente den Zweig „Keine Zusammenhaenge" — das beobachtete `div`
  // existiert in dem Moment gar nicht. `box.current` war `null`, der Effekt
  // stieg aus und lief nie wieder: die Breite blieb fuer immer auf dem
  // Startwert 880 stehen.
  //
  // Auf dev nachgemessen: Container 787 px breit, viewBox trotzdem 880 — das
  // SVG wurde also auf 0,89 heruntergerechnet, und alles wirkte kleiner als
  // noetig. Bei einem breiten Fenster (1009 px) andersherum auf 1,15 gedehnt.
  // Genau der „zu klein / zentriert"-Eindruck aus der Meldung.
  const beobachter = useRef<ResizeObserver | null>(null)
  const boxRef = useCallback((el: HTMLDivElement | null) => {
    box.current = el
    beobachter.current?.disconnect()
    if (!el) return
    const ro = new ResizeObserver((eintraege) => {
      const b = eintraege[0]?.contentRect.width
      if (b && b > 200) setBreiteVerfuegbar(b)
    })
    ro.observe(el)
    beobachter.current = ro
    // Sofort einmal selbst messen: der Beobachter meldet sich zwar direkt
    // nach `observe`, aber erst im naechsten Frame.
    const b = el.getBoundingClientRect().width
    if (b > 200) setBreiteVerfuegbar(b)
  }, [])
  useEffect(() => () => beobachter.current?.disconnect(), [])

  // Verfuegbare Hoehe = von der Oberkante des Feldes bis zum Fensterboden.
  //
  // 🔑 Gemessen, nicht gerechnet — die Lehre aus dem Responsive-Umbau. Eine
  // feste Pixelkonstante waere hier wieder derselbe Fehler.
  //
  // Der Container selbst taugt nicht als Massstab: seine Hoehe kommt vom SVG,
  // das SVG von dieser Zahl — das waere ein Kreis. Darum der Viewport.
  // Gedeckelt, weil ein sehr hohes Fenster das Netz sonst in die Laenge zieht,
  // ohne dass mehr hineinpasst; und nie kleiner als der uebergebene Wert.
  useEffect(() => {
    const messen = () => {
      setHoeheVerfuegbar(
        Math.max(height, Math.min(Math.round(window.innerHeight * HOEHE_ANTEIL), MAX_HOEHE)),
      )
    }
    messen()
    window.addEventListener("resize", messen)
    return () => window.removeEventListener("resize", messen)
  }, [height])

  const layout = useMemo(() => {
    // Erst filtern, dann rechnen. Ein ausgeblendeter Vorgang nimmt seinen
    // ganzen Zweig mit — sonst haengen dessen Belege und Mails plötzlich
    // im Nichts.
    const rohNodes = graph?.nodes || []
    const rohEdges = graph?.edges || []
    const weg = new Set<string>()
    for (const n of rohNodes) {
      if (!zeigeAbgeschlossene && n.kind === "project" && n.closed) weg.add(n.id)
      if (versteckteArten.has(n.kind)) weg.add(n.id)
    }
    // Kinder ausgeblendeter Knoten mit entfernen (Baum, also ein Durchlauf
    // je Ebene bis nichts Neues mehr dazukommt).
    let gewachsen = true
    while (gewachsen) {
      gewachsen = false
      for (const e of rohEdges) {
        if (weg.has(e.from) && !weg.has(e.to)) { weg.add(e.to); gewachsen = true }
      }
    }
    const nodes = rohNodes.filter((n) => !weg.has(n.id))
    const edges = rohEdges.filter((e) => !weg.has(e.from) && !weg.has(e.to))
    if (!nodes.length) return null

    const kinder = new Map<string, string[]>()
    const eltern = new Map<string, string>()
    for (const e of edges) {
      if (!kinder.has(e.from)) kinder.set(e.from, [])
      kinder.get(e.from)!.push(e.to)
      eltern.set(e.to, e.from)
    }
    // Wurzel: der Knoten ohne Eltern. Faellt auf den ersten zurueck, damit
    // ein Dossier ohne Kontakt (unbekannte Nummer) trotzdem zeichnet.
    const wurzel = nodes.find((n) => !eltern.has(n.id)) || nodes[0]
    const proId = new Map(nodes.map((n) => [n.id, n]))

    // Wie tief geht der Baum? Seit E-Mail-Threads als Kette haengen (Antwort
    // unter der Mail, auf die sie antwortet), sind es mehr als die frueheren
    // drei festen Ringe. Die Radien richten sich nach der tatsaechlichen Tiefe.
    const tiefeVon = new Map<string, number>([[wurzel.id, 0]])
    const warteschlange = [wurzel.id]
    let maxTiefe = 0
    while (warteschlange.length) {
      const id = warteschlange.shift()!
      const t = tiefeVon.get(id)!
      maxTiefe = Math.max(maxTiefe, t)
      for (const k of kinder.get(id) || []) {
        // Zyklen kann es nicht geben (Baum), der Schutz kostet aber nichts.
        if (tiefeVon.has(k)) continue
        tiefeVon.set(k, t + 1)
        warteschlange.push(k)
      }
    }

    // Radien aus dem verfuegbaren Platz ableiten. X und Y getrennt, weil das
    // Feld breiter als hoch ist — daraus entsteht die Ellipse, die genau in
    // der Richtung mehr Raum gibt, in der Text ihn braucht.
    const rX = Math.max(150, breiteVerfuegbar / 2 - TEXT_RAND_X)
    const rY = Math.max(110, hoeheVerfuegbar / 2 - TEXT_RAND_Y)
    const stufen = Math.max(1, maxTiefe)
    const radius = (t: number) => {
      if (t <= 0) return { x: 0, y: 0 }
      // Der erste Ring sitzt naeher an der Mitte als die gleichmaessige
      // Aufteilung ergaebe — dort steht die Wurzelbeschriftung.
      const anteil = 0.42 + 0.58 * ((t - 1) / Math.max(1, stufen - 1))
      return { x: rX * (stufen === 1 ? 1 : anteil), y: rY * (stufen === 1 ? 1 : anteil) }
    }
    const RING_X = [0, radius(1).x, radius(stufen).x]
    const RING_Y = [0, radius(1).y, radius(stufen).y]

    const platziert: Platziert[] = []
    platziert.push({ ...wurzel, x: 0, y: 0, r: KNOTEN_R[0], ring: 0, winkel: 0 })

    const knotenGroesse = (t: number, hatKinder: boolean) => {
      if (t === 0) return KNOTEN_R[0]
      if (t === 1) return hatKinder ? KNOTEN_R[1] : KNOTEN_R[1] - 5
      return KNOTEN_R[2]
    }

    /**
     * Setzt einen Teilbaum rekursiv.
     *
     * `mitte` ist die Richtung, in die der Zweig zeigt, `spanne` der Winkel,
     * der ihm zur Verfuegung steht. Kinder faechern sich um die Richtung des
     * Elternteils auf — begrenzt, sonst stehen die aeusseren so schraeg, dass
     * die Zugehoerigkeit nicht mehr zu sehen ist.
     */
    const setzeKinder = (id: string, mitte: number, spanne: number) => {
      const liste = (kinder.get(id) || []).map((k) => proId.get(k)!).filter(Boolean)
      if (!liste.length) return
      const nutzbar = Math.min(spanne * 0.8, MAX_FAECHER)
      const start = mitte - nutzbar / 2
      const schritt = liste.length > 1 ? nutzbar / (liste.length - 1) : 0
      liste.forEach((k, i) => {
        const a = liste.length === 1 ? mitte : start + schritt * i
        const t = tiefeVon.get(k.id) ?? 2
        const r = radius(t)
        // Jeder zweite Knoten einer Ebene etwas weiter innen — reine
        // Massnahme gegen uebereinanderliegende Beschriftungen.
        const f = liste.length > 3 && i % 2 ? 1 - VERSATZ_ANTEIL : 1
        platziert.push({
          ...k,
          x: Math.cos(a) * r.x * f,
          y: Math.sin(a) * r.y * f,
          r: knotenGroesse(t, (kinder.get(k.id)?.length || 0) > 0),
          ring: t,
          winkel: a,
        })
        setzeKinder(k.id, a, liste.length === 1 ? nutzbar : schritt || nutzbar)
      })
    }

    // Zweige des Wurzelknotens GLEICHMAESSIG auf den Kreis verteilen — nicht
    // nach Anzahl ihrer Kinder gewichtet.
    //
    // Der erste Entwurf tat genau das (Sektor proportional zur Kinderzahl) und
    // wurde bei ungleichen Zweigen unbrauchbar: ein Vorgang mit acht Belegen
    // gegen eine einzelne lose Mail ergab einen 320°-Sektor, dessen Mitte den
    // Vorgang weit aus der Achse schob — alles klebte in einer Ecke. Gleiche
    // Winkel sind vorhersehbar; die unterschiedliche Fuelle zeigt sich in der
    // Breite des Faechers, nicht in der Position.
    const ring1 = (kinder.get(wurzel.id) || [])
      .map((id) => proId.get(id)!)
      .filter(Boolean)
      .sort((a, b) => (kinder.get(b.id)?.length || 0) - (kinder.get(a.id)?.length || 0))
    const schrittWinkel = (Math.PI * 2) / Math.max(1, ring1.length)
    // Startwinkel oben (-90°), damit das Bild bei einem einzigen Vorgang
    // senkrecht steht und nicht schief nach rechts kippt.
    ring1.forEach((n, i) => {
      const mitte = -Math.PI / 2 + schrittWinkel * i
      const r = radius(1)
      platziert.push({
        ...n,
        x: Math.cos(mitte) * r.x,
        y: Math.sin(mitte) * r.y,
        r: knotenGroesse(1, n.kind === "project" || (kinder.get(n.id)?.length || 0) > 0),
        ring: 1,
        winkel: mitte,
      })
      setzeKinder(n.id, mitte, schrittWinkel)
    })

    const posProId = new Map(platziert.map((p) => [p.id, p]))
    const kanten = edges
      .map((e) => ({ a: posProId.get(e.from), b: posProId.get(e.to) }))
      .filter((k) => k.a && k.b) as Array<{ a: Platziert; b: Platziert }>

    // Der viewBox ist exakt so gross wie der Container — Massstab 1:1.
    const halbBreite = breiteVerfuegbar / 2
    const halbHoehe = hoeheVerfuegbar / 2
    return {
      platziert, kanten, eltern, RING_X, RING_Y,
      viewBox: `${-halbBreite} ${-halbHoehe} ${breiteVerfuegbar} ${hoeheVerfuegbar}`,
    }
  }, [graph, breiteVerfuegbar, hoeheVerfuegbar, zeigeAbgeschlossene, versteckteArten])

  // 🔴 Alle Hooks VOR dem Early-Return unten. Stehen sie dahinter, aendert
  // sich die Zahl der Hooks zwischen „kein Graph" und „Graph da" — React
  // bricht dann beim Umschalten ab. (Genau der Fehler, den die erste Fassung
  // dieses Fixes hatte.)
  const platzierteKnoten = layout?.platziert ?? LEER
  const zeigeAlle = alleLabels || platzierteKnoten.length <= 22

  // Ticket #20: Beschriftungen auseinanderruecken. Haengt an `zeigeAlle`, weil
  // sich mit den eingeblendeten Blaettern auch die Nachbarschaft aendert —
  // NICHT an `aktiv`: sonst huepften beim Ueberfahren alle Texte.
  const labelVersatz = useMemo(
    () => entzerreLabels(platzierteKnoten, zeigeAlle),
    [platzierteKnoten, zeigeAlle],
  )
  // Wie eng der erste Ring besetzt ist — steuert, wie kurz die Zweig-Namen
  // gesetzt werden. Muss dieselbe Zahl sein, mit der `entzerreLabels` rechnet.
  const zweigAnzahl = useMemo(
    () => platzierteKnoten.filter((p) => p.ring === 1).length,
    [platzierteKnoten],
  )

  if (!layout) {
    return (
      <div ref={boxRef} style={{
        height, display: "flex", alignItems: "center", justifyContent: "center",
        color: "#94a3b8", fontSize: 13,
      }}>
        Keine Zusammenhänge zum Anzeigen.
      </div>
    )
  }

  const { platziert, kanten, viewBox, RING_X, RING_Y } = layout

  // Hervorhebung: der aktive Knoten, seine Eltern und seine Kinder bleiben
  // voll sichtbar, alles andere blasst ab. Ohne das ist bei 40 Knoten nicht
  // erkennbar, was zusammengehoert.
  const verwandt = (id: string): boolean => {
    if (!aktiv) return true
    if (id === aktiv) return true
    return kanten.some(
      (k) => (k.a.id === aktiv && k.b.id === id) || (k.b.id === aktiv && k.a.id === id),
    )
  }

  // Wie viele Vorgaenge waeren abgeschlossen? Nur dann lohnt der Schalter.
  const abgeschlossene = (graph?.nodes || []).filter(
    (n) => n.kind === "project" && n.closed,
  ).length
  const vorhandeneArten = Array.from(
    new Set((graph?.nodes || []).map((n) => n.kind)),
  ).filter((a) => a !== "contact" && a !== "more")

  const artUmschalten = (a: string) => {
    setVersteckteArten((alt) => {
      const neu = new Set(alt)
      if (neu.has(a)) neu.delete(a); else neu.add(a)
      return neu
    })
  }

  return (
    <div ref={boxRef} style={{ position: "relative" }}>
      {/* Filterleiste — bei einem Kunden mit drei Vorgaengen und 17 Mails ist
          das Bild sonst nicht mehr zu lesen. */}
      <div style={{
        display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap",
        marginBottom: 8, paddingBottom: 8, borderBottom: "1px solid #f1f5f9",
      }}>
        {abgeschlossene > 0 && (
          <button
            onClick={() => setZeigeAbgeschlossene((v) => !v)}
            title="Abgeschlossene Vorgänge samt allem, was daran hängt"
            style={{
              display: "inline-flex", alignItems: "center", gap: 5, fontSize: 12,
              fontWeight: 600, padding: "4px 9px", borderRadius: 7, cursor: "pointer",
              border: "1px solid " + (zeigeAbgeschlossene ? "#cbd5e1" : "#e2e8f0"),
              background: zeigeAbgeschlossene ? "#f1f5f9" : "#fff",
              color: zeigeAbgeschlossene ? "#0f172a" : "#64748b",
            }}
          >
            {zeigeAbgeschlossene ? "Abgeschlossene ausblenden" : `+ ${abgeschlossene} abgeschlossen`}
          </button>
        )}
        <span style={{ width: 1, height: 16, background: "#e2e8f0" }} />
        {vorhandeneArten.map((a) => {
          const aus = versteckteArten.has(a)
          return (
            <button
              key={a}
              onClick={() => artUmschalten(a)}
              style={{
                display: "inline-flex", alignItems: "center", gap: 5, fontSize: 12,
                fontWeight: 600, padding: "4px 9px", borderRadius: 7, cursor: "pointer",
                border: "1px solid " + (aus ? "#e2e8f0" : `${FARBE[a]}55`),
                background: aus ? "#fff" : `${FARBE[a]}14`,
                color: aus ? "#cbd5e1" : FARBE[a],
                textDecoration: aus ? "line-through" : "none",
              }}
            >
              <span style={{
                width: 8, height: 8, borderRadius: "50%",
                background: aus ? "#e2e8f0" : FARBE[a],
              }} />
              {ART_NAME[a]}
            </button>
          )
        })}
        <span style={{ marginLeft: "auto", display: "inline-flex", alignItems: "center", gap: 10,
                       fontSize: 12, color: "#94a3b8" }}>
          {/* Ohne diesen Satz sind die Zahlen an den Knoten eine Zumutung —
              man raet, ob sie eine Reihenfolge, eine Anzahl oder eine ID sind. */}
          <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
            <span style={{
              width: 14, height: 14, borderRadius: "50%", background: "#0f172a", color: "#fff",
              fontSize: 12, fontWeight: 700, display: "inline-flex",
              alignItems: "center", justifyContent: "center",
            }}>{zeitRang.size || 1}</span>
            {aeltereOhneNummer > 0 ? `Reihenfolge der letzten ${NUMMERN_FENSTER_MONATE} Monate` : "zeitliche Reihenfolge"}, dunkel = letzter Stand
          </span>
          <span>Mausrad zoomt · ziehen verschiebt · Doppelklick setzt zurück</span>
        </span>
      </div>

      <svg
        viewBox={viewBox}
        style={{
          width: "100%", height: hoeheVerfuegbar, display: "block",
          cursor: zieht.current ? "grabbing" : "grab",
          touchAction: "none",
        }}
        onMouseLeave={() => { setAktiv(null); zieht.current = null }}
        onWheel={(ev) => {
          // Ohne preventDefault scrollt die Seite mit. React haengt onWheel
          // passiv an, darum zusaetzlich der native Listener unten.
          const richtung = ev.deltaY < 0 ? 1.12 : 1 / 1.12
          setZoom((z) => Math.min(4, Math.max(0.5, z * richtung)))
        }}
        onMouseDown={(ev) => { zieht.current = { x: ev.clientX, y: ev.clientY } }}
        onMouseUp={() => { zieht.current = null }}
        onMouseMove={(ev) => {
          if (!zieht.current) return
          const dx = ev.clientX - zieht.current.x
          const dy = ev.clientY - zieht.current.y
          zieht.current = { x: ev.clientX, y: ev.clientY }
          setVersatz((v) => ({ x: v.x + dx, y: v.y + dy }))
        }}
        onDoubleClick={() => { setZoom(1); setVersatz({ x: 0, y: 0 }) }}
      >
        <g transform={`translate(${versatz.x},${versatz.y}) scale(${zoom})`}>
        {/* Orientierungsringe — ohne sie schwebt das Bild im Nichts.
            Ellipsen, weil das Layout waagerecht gedehnt ist. */}
        {[1, 2].map((i) => (
          <ellipse
            key={i} cx={0} cy={0} rx={RING_X[i]} ry={RING_Y[i]}
            fill="none" stroke="#f1f5f9" strokeWidth={1.5}
          />
        ))}

        {kanten.map((k, i) => {
          const hell = verwandt(k.a.id) && verwandt(k.b.id)
          return (
            <line
              key={i}
              x1={k.a.x} y1={k.a.y} x2={k.b.x} y2={k.b.y}
              stroke={hell ? FARBE[k.b.kind] : "#e2e8f0"}
              strokeWidth={k.b.ring === 1 ? 2 : 1.2}
              strokeOpacity={hell ? (k.b.ring === 1 ? 0.5 : 0.32) : 0.12}
            />
          )
        })}

        {platziert.map((p) => {
          const hell = verwandt(p.id)
          const farbe = FARBE[p.kind]
          const anklickbar = p.kind !== "more" && !!p.ref_id
          const labelZeigen = p.ring <= 1 || zeigeAlle || p.id === aktiv
          return (
            <g
              key={p.id}
              transform={`translate(${p.x},${p.y})`}
              opacity={hell ? 1 : 0.25}
              style={{ cursor: anklickbar ? "pointer" : "default" }}
              onMouseEnter={() => setAktiv(p.id)}
              onClick={() => anklickbar && onSelect?.(p)}
            >
              {p.id === aktiv && (
                <circle r={p.r + 6} fill={farbe} opacity={0.14} />
              )}
              <circle
                r={p.r}
                fill={p.ring === 0 ? farbe : "#fff"}
                stroke={farbe}
                strokeWidth={p.ring === 0 ? 2 : p.ring === 1 ? 2.5 : 2}
              />
              {/* Der letzte Stand bekommt einen Ring — die eine Frage, die man
                  an einen Verlauf zuerst stellt, soll ohne Suchen beantwortet
                  sein. */}
              {p.id === letzterKnoten && (
                <circle r={p.r + 6} fill="none" stroke={farbe} strokeWidth={1.5} strokeOpacity={0.45} />
              )}
              {zeitRang.has(p.id) && <Zeitmarke p={p} nr={zeitRang.get(p.id)!} letzter={p.id === letzterKnoten} />}
              {p.ring < 2 && (
                <g transform={`translate(${-(p.ring === 0 ? 9 : 6)},${-(p.ring === 0 ? 9 : 6)})`}>
                  <foreignObject width={p.ring === 0 ? 18 : 12} height={p.ring === 0 ? 18 : 12}>
                    <KindIcon
                      kind={p.kind}
                      size={p.ring === 0 ? 18 : 12}
                      color={p.ring === 0 ? "#fff" : farbe}
                    />
                  </foreignObject>
                </g>
              )}
              {/* Blaetter werden RADIAL nach aussen beschriftet, nicht
                  darunter: bei acht Punkten in einem 75°-Faecher liegen
                  zentrierte Texte sonst uebereinander. Wurzel und Zweige
                  behalten die Beschriftung mittig darunter — dort ist Platz. */}
              {labelZeigen && p.ring >= 2 && (
                <>
                  <text
                    x={Math.cos(p.winkel) * (p.r + 7 + versatzAussen(p))}
                    y={Math.sin(p.winkel) * (p.r + 7 + versatzAussen(p)) + (p.date ? -1 : 3.5)
                       + (labelVersatz.get(p.id) || 0)}
                    textAnchor={Math.cos(p.winkel) >= 0 ? "start" : "end"}
                    style={{
                      fontSize: 12, fontWeight: 500, fill: "#475569",
                      paintOrder: "stroke", stroke: "#fff", strokeWidth: 3.5,
                      strokeLinejoin: "round",
                    }}
                  >
                    {kurz(p.label, labelMax(p, zweigAnzahl))}
                  </text>
                  {/* Datum als eigene Zeile — auf einen Blick erkennbar, ohne
                      dass es mit dem Betreff verschmilzt. */}
                  {p.date && (
                    <text
                      x={Math.cos(p.winkel) * (p.r + 7 + versatzAussen(p))}
                      y={Math.sin(p.winkel) * (p.r + 7 + versatzAussen(p)) + 10
                         + (labelVersatz.get(p.id) || 0)}
                      textAnchor={Math.cos(p.winkel) >= 0 ? "start" : "end"}
                      style={{
                        fontSize: 12, fontWeight: 600, fill: "#94a3b8",
                        paintOrder: "stroke", stroke: "#fff", strokeWidth: 3,
                        strokeLinejoin: "round",
                      }}
                    >
                      {kurzDatum(p.date)}
                    </text>
                  )}
                </>
              )}
              {labelZeigen && p.ring < 2 && (
                <text
                  x={0}
                  y={p.r + (p.ring === 0 ? 20 : 15) + (labelVersatz.get(p.id) || 0)}
                  textAnchor="middle"
                  style={{
                    fontSize: p.ring === 0 ? 15 : 12.5,
                    fontWeight: p.ring === 0 ? 700 : 600,
                    fill: "#0f172a",
                    paintOrder: "stroke",
                    stroke: "#fff",
                    strokeWidth: 3.5,
                    strokeLinejoin: "round",
                  }}
                >
                  {kurz(p.label, labelMax(p, zweigAnzahl))}
                </text>
              )}
              {p.ring < 2 && (p.sublabel || p.date) && labelZeigen && (
                <text
                  x={0}
                  y={p.r + (p.ring === 0 ? 36 : 29) + (labelVersatz.get(p.id) || 0)}
                  textAnchor="middle"
                  style={{
                    fontSize: p.ring === 0 ? 11.5 : 10.5, fill: "#64748b",
                    paintOrder: "stroke", stroke: "#fff", strokeWidth: 3,
                    strokeLinejoin: "round",
                  }}
                >
                  {sublabelText(p)}
                </text>
              )}
            </g>
          )
        })}
        </g>
      </svg>

      {platziert.length > 22 && (
        <button
          onClick={() => setAlleLabels((v) => !v)}
          style={{
            position: "absolute", right: 0, bottom: 0, fontSize: 12, color: "#475569",
            background: "#fff", border: "1px solid #e2e8f0", borderRadius: 7,
            padding: "4px 9px", cursor: "pointer",
          }}
        >
          {alleLabels ? "Beschriftungen aus" : "Alle beschriften"}
        </button>
      )}
    </div>
  )
}

/** Nur fuer Tests: die Entzerrung ist reine Rechnung und laesst sich ohne
 *  gerendertes SVG pruefen. Ueber die Komponente ginge es nur mit jsdom-
 *  Textmessung — die gibt es dort nicht. */
export const __test__ = { entzerreLabels, labelHuelle, textBreite, labelMax, MAX_VERSATZ }
